import React, { useEffect, useMemo, useState } from 'react';
import { PlusIcon } from '@heroicons/react/24/outline';
import { BackendApi } from '../../../../../api/api.ts';
import { User } from '../../../../../api/accessControl/User.ts';
import Shell from '../../../../../components/specific/Shell.tsx';
import { PrimaryButton } from '../../../../../components/general/PrimaryButton.tsx';
import Popup from '../../../../../components/general/Popup.tsx';
import { useNotifications } from '../../../../../lib/NotificationContext.tsx';
import CatalogTable from './CatalogTable.tsx';
import CatalogItemModal from './CatalogItemModal.tsx';
import { CatalogItem, CatalogItemFormValues } from './types.ts';
import {
    changeCatalogItem,
    createCatalogItem,
    listCatalogItems,
} from './catalogApi.ts';
import { extractApiErrorMessage } from '../../../../../lib/apiErrors.ts';

export default function CatalogPage({ currentUser }: { currentUser: User }) {
    const api = useMemo(() => new BackendApi(currentUser.getToken()), [currentUser]);

    const [items, setItems] = useState<CatalogItem[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingItem, setEditingItem] = useState<CatalogItem | null>(null);
    const [itemPendingDeletion, setItemPendingDeletion] = useState<CatalogItem | null>(null);
    const [isDeleting, setIsDeleting] = useState(false);
    const { dispatch } = useNotifications();

    async function fetchItems() {
        setIsLoading(true);
        try {
            setItems(await listCatalogItems(api));
        } catch (error) {
            console.error('Error loading catalog items:', error);
            dispatch({
                title: 'Laden mislukt',
                description: 'Je catalogus kon niet worden geladen. Probeer het later opnieuw.',
                type: 'error',
            });
        } finally {
            setIsLoading(false);
        }
    }

    useEffect(() => {
        fetchItems();
    }, [api]);

    function openCreateModal() {
        setEditingItem(null);
        setIsModalOpen(true);
    }

    function openEditModal(item: CatalogItem) {
        setEditingItem(item);
        setIsModalOpen(true);
    }

    async function handleSubmit(values: CatalogItemFormValues) {
        try {
            if (editingItem) {
                await changeCatalogItem(api, editingItem.id, values);
            } else {
                await createCatalogItem(api, values);
            }
            await fetchItems();
            setIsModalOpen(false);
            setEditingItem(null);
            dispatch({
                title: editingItem ? 'Item bijgewerkt' : 'Item toegevoegd',
                description: editingItem
                    ? 'De wijzigingen zijn opgeslagen.'
                    : 'Het item is toegevoegd aan je catalogus.',
                type: 'success',
            });
        } catch (error: unknown) {
            dispatch({
                title: 'Opslaan mislukt',
                description:
                    extractApiErrorMessage(error) ??
                    'Er is een fout opgetreden. Probeer het later opnieuw.',
                type: 'error',
            });
            throw error;
        }
    }

    async function handleDeleteConfirmed() {
        if (!itemPendingDeletion || isDeleting) {
            return;
        }
        setIsDeleting(true);
        try {
            await api.Me__business__archiveCatalogueItem({ item_id: itemPendingDeletion.id });
            setItemPendingDeletion(null);
            await fetchItems();
            dispatch({
                title: 'Item verwijderd',
                description: 'Het item is uit je catalogus verwijderd.',
                type: 'success',
            });
        } catch (error) {
            console.error('Error archiving catalog item:', error);
            dispatch({
                title: 'Verwijderen mislukt',
                description:
                    extractApiErrorMessage(error) ??
                    'Er is een fout opgetreden. Probeer het later opnieuw.',
                type: 'error',
            });
        } finally {
            setIsDeleting(false);
        }
    }

    const takenNames = items
        .filter((item) => item.id !== editingItem?.id)
        .map((item) => item.name.trim().toLowerCase());

    return (
        <Shell
            currentUser={currentUser}
            title="Producten & diensten"
            subtitle="Beheer de items die je op je verkoopfacturen gebruikt: prijzen, eenheden en btw-tarieven."
            titleActions={
                <PrimaryButton title="Nieuw item" icon={PlusIcon} onClick={openCreateModal} />
            }
        >
            <CatalogTable
                items={items}
                isLoading={isLoading}
                onEdit={openEditModal}
                onDelete={setItemPendingDeletion}
                onCreateClick={openCreateModal}
            />

            <CatalogItemModal
                open={isModalOpen}
                item={editingItem}
                takenNames={takenNames}
                onSubmit={handleSubmit}
                onClose={() => {
                    setIsModalOpen(false);
                    setEditingItem(null);
                }}
            />

            <Popup
                open={itemPendingDeletion !== null}
                onClose={() => setItemPendingDeletion(null)}
                onConfirm={handleDeleteConfirmed}
                title="Item verwijderen"
                confirmButtonDisplayLabel="Verwijderen"
                confirmButtonDisabled={isDeleting}
                variant="danger"
            >
                <p className="text-sm text-gray-500">
                    Weet je zeker dat je <span className="font-medium text-gray-900">{itemPendingDeletion?.name}</span> wilt
                    verwijderen uit je catalogus?
                    {!!itemPendingDeletion && itemPendingDeletion.times_used > 0 && (
                        <> Dit item is al {itemPendingDeletion.times_used}× gebruikt — bestaande facturen blijven ongewijzigd.</>
                    )}
                </p>
            </Popup>
        </Shell>
    );
}
